// @flow

import React from 'react';
import memoize from 'memoize-weak';
import { filter } from 'lodash';

import { toLegacyCompatibleService, WithAllServices } from '@zapier/services';

import { blacklistedServices } from 'app/service/constants';

import type { ComponentType, Node } from 'react';

import type { Service } from 'app/service/types';

const filterPublicSearchableServices: (
  services: Array<Service>
) => Array<Service> = memoize(services =>
  filter(
    services,
    service =>
      service.isPublic &&
      !service.isUpcoming &&
      // `isHidden` services are services that might be used but are no longer "official".
      // e.g. python apps that might be technically available for past zaps but not for new apps.
      // We don't want those to show up on searches.
      !service.isHidden &&
      !service.isDeprecated &&
      blacklistedServices[service.key] !== true // filter any blacklisted apis
  )
);

type Props = {
  children: ({
    data: { services: Array<Service>, loading: boolean, error: ?Error },
  }) => Node,
};

const WithPublicSearchableServicesData = (props: Props) => {
  return (
    <WithAllServices includePrivate={false}>
      {({ services, loading, error }) => {
        const filteredServices = filterPublicSearchableServices(services);

        const data = {
          loading,
          error,
          services: filteredServices.map(toLegacyCompatibleService),
        };

        return props.children({ data });
      }}
    </WithAllServices>
  );
};

// hoc'ified `WithPublicSearchableServicesData` for compatibility
// with legacy code
export const withPublicSearchableServicesData = (
  WrappedComponent: ComponentType<any>
) => {
  return (props: *) => (
    <WithPublicSearchableServicesData>
      {childProps => {
        return <WrappedComponent {...props} {...childProps} />;
      }}
    </WithPublicSearchableServicesData>
  );
};
